import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { cilMenu } from "@coreui/icons";
import CIcon from "@coreui/icons-react";
import {
  CContainer,
  CHeader,
  CHeaderNav,
  CHeaderToggler,
  CNavItem,
  CNavLink,
  CNavbarBrand,
  CSidebar,
  CSidebarNav,
  CSpinner,
} from "@coreui/react";
import { AppsLinks } from "@src/components/AppsLinks";
import { useJobPolling, useLayout } from "@src/hooks/redux";
import type { User } from "@src/services/api/tdApi";
import { versionInfo } from "@src/utils/version";
import { ThemeSwitcher, useDarkMode } from "@tradingdroid/frontends-components";

// Job polling interval in milliseconds (matches JobPollingManager.tsx)
const POLLING_INTERVAL_MS = 3000;
const POLLING_INTERVAL_SECONDS = POLLING_INTERVAL_MS / 1000;

interface LayoutProps {
  children: ReactNode;
  user: User;
  onLogout: () => void;
}

export const Layout = ({ children, user, onLogout }: LayoutProps) => {
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [sidebarNarrow, setSidebarNarrow] = useState(false);
  const location = useLocation();
  const { isDark } = useDarkMode();
  const { pageTitle, setPageTitle } = useLayout();
  const { activeJobs } = useJobPolling();

  const isPollingJobs = activeJobs.length > 0;

  const toggleSidebar = () => {
    // Check if mobile (matches CoreUI's mobile breakpoint: < 992px)
    const isMobile = window.innerWidth < 992;

    if (isMobile) {
      // Mobile: toggle visibility only, keep narrow=false for full width
      setSidebarVisible(!sidebarVisible);
    } else {
      // Desktop: toggle narrow only (visibility always true via CSS)
      setSidebarNarrow(!sidebarNarrow);
    }
  };

  useEffect(() => {
    const path = location.pathname;
    const titleMap: Array<{ match: RegExp; title: string }> = [
      { match: /^\/portfolio$/, title: "Portfolio" },
      { match: /^\/executions$/, title: "Executions" },
      { match: /^\/exec-groups$/, title: "Execution Groups" },
      { match: /^\/exec-group\//, title: "Execution Group" },
      { match: /^\/execution\//, title: "Execution" },
      { match: /^\/backtests$/, title: "Backtests" },
      { match: /^\/backtest\/new$/, title: "Run Backtest" },
      { match: /^\/backtest\//, title: "Backtest Details" },
      { match: /^\/studies$/, title: "Studies" },
      { match: /^\/optimization\/new$/, title: "New Optimization" },
      { match: /^\/study\//, title: "Study Results" },
      { match: /^\/jobs$/, title: "Jobs" },
      { match: /^\/playground$/, title: "Playground" },
      { match: /^\/prototyping$/, title: "Strategy Prototyping" },
      { match: /^\/settings$/, title: "User Settings" },
    ];

    const matched = titleMap.find((entry) => entry.match.test(path));
    setPageTitle(matched?.title ?? "TradingDroid");
  }, [location.pathname, setPageTitle]);

  const navigation = [
    {
      component: CNavItem,
      name: "Portfolio",
      to: "/portfolio",
      icon: "📊",
    },
    {
      component: CNavItem,
      name: "Executions",
      to: "/executions",
      icon: "⚡",
    },
    {
      component: CNavItem,
      name: "Backtests",
      to: "/backtests",
      icon: "📋",
    },
    {
      component: CNavItem,
      name: "Studies",
      to: "/studies",
      icon: "🎯",
    },
    {
      component: CNavItem,
      name: "Jobs",
      to: "/jobs",
      icon: "⚙️",
    },
    {
      component: CNavItem,
      name: "Prototyping",
      to: "/prototyping",
      icon: "🧪",
    },
    // {
    //   component: CNavItem,
    //   name: "Playground",
    //   to: "/playground",
    //   icon: "🎮",
    // },
  ];

  return (
    <div id="AppLayout" className="d-flex">
      <CSidebar
        id="AppSidebar"
        className="sidebar"
        colorScheme={isDark ? "dark" : "light"}
        visible={sidebarVisible}
        narrow={sidebarNarrow}
        onVisibleChange={setSidebarVisible}
      >
        <CSidebarNav>
          <CNavItem className="px-3 py-2">
            <CNavbarBrand href="#" className={isDark ? "text-white" : "text-dark"}>
              {sidebarNarrow ? (
                <div className="d-flex justify-content-center">
                  <span title="TradingDroid Workstation">⚙️</span>
                </div>
              ) : (
                "TradingDroid Workstation"
              )}
            </CNavbarBrand>
          </CNavItem>

          {navigation.map((item) => {
            const isJobsItem = item.to === "/jobs";
            const showPollingIndicator = isJobsItem && isPollingJobs;

            return (
              <CNavItem key={item.to}>
                <CNavLink
                  as={Link}
                  to={item.to}
                  active={location.pathname === item.to}
                  className={`d-flex align-items-center ${sidebarNarrow ? "justify-content-center" : ""}`}
                  title={
                    sidebarNarrow
                      ? showPollingIndicator
                        ? `${item.name} (${activeJobs.length} polling @ ${POLLING_INTERVAL_SECONDS}s)`
                        : item.name
                      : undefined
                  }
                >
                  <span className={sidebarNarrow ? "" : "me-2"}>{item.icon}</span>
                  {!sidebarNarrow && (
                    <>
                      {item.name}
                      {showPollingIndicator && (
                        <span className="ms-2 d-flex align-items-center text-info">
                          <CSpinner
                            size="sm"
                            className="me-1"
                            style={{ width: "12px", height: "12px" }}
                          />
                          <small>({activeJobs.length})</small>
                        </span>
                      )}
                    </>
                  )}
                  {sidebarNarrow && showPollingIndicator && (
                    <CSpinner
                      size="sm"
                      className="position-absolute"
                      style={{ width: "8px", height: "8px", top: "4px", right: "4px" }}
                    />
                  )}
                </CNavLink>
              </CNavItem>
            );
          })}

          <CNavItem className="mt-auto">
            <CNavLink
              as={Link}
              to="/settings"
              active={location.pathname === "/settings"}
              className={`d-flex align-items-center ${sidebarNarrow ? "justify-content-center" : ""}`}
              title={sidebarNarrow ? "User Settings" : undefined}
            >
              <span className={sidebarNarrow ? "" : "me-2"}>👤</span>
              {!sidebarNarrow && "User Settings"}
            </CNavLink>
          </CNavItem>

          <CNavItem className="AppVersionInfo px-3 py-2">
            <div className="small text-muted" title="App Version">
              <small>{`v ${versionInfo.version} (bun ${versionInfo.bunVersion})`}</small>
              <br />
              <small>@ {versionInfo.buildTimestamp}</small>
            </div>
          </CNavItem>
        </CSidebarNav>
      </CSidebar>

      <div id="AppWrapper" className="d-flex flex-column w-100">
        <CHeader id="AppHeader" className="header">
          <CContainer fluid className="d-flex align-items-center justify-content-between gap-3">
            <div className="d-flex align-items-center gap-3">
              <CHeaderToggler className="ps-1" onClick={toggleSidebar}>
                <CIcon icon={cilMenu} size="lg" />
              </CHeaderToggler>
              <h1 className="h5 mb-0">{pageTitle}</h1>
              {location.pathname === "/executions" && (
                <Link to="/exec-groups" className="PageTitleSecondaryLink ms-5 small text-muted">
                  Groups
                </Link>
              )}
            </div>
            <CHeaderNav className="ms-auto d-flex align-items-center gap-3">
              <ThemeSwitcher />

              <AppsLinks onLogout={onLogout} />
            </CHeaderNav>
          </CContainer>
        </CHeader>

        <div id="AppContent" className="body flex-grow-1 py-2">
          <CContainer fluid>{children}</CContainer>
        </div>
      </div>
    </div>
  );
};
